import { NgIf } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, Directive, ContentChild, ViewContainerRef, forwardRef, createComponent, EnvironmentInjector, INJECTOR, Input, signal, Component, ChangeDetectionStrategy, NgZone, DestroyRef, ChangeDetectorRef, isSignal, ViewEncapsulation, ContentChildren } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EMPTY_QUERY } from '@taiga-ui/cdk/constants';
import { tuiZonefree, tuiTakeUntilDestroyed } from '@taiga-ui/cdk/observables';
import { tuiInjectElement } from '@taiga-ui/cdk/utils/dom';
import { tuiIsNativeFocused, tuiMoveFocus, tuiIsNativeFocusedIn } from '@taiga-ui/cdk/utils/focus';
import { tuiCreateToken, tuiProvide, tuiIsPresent } from '@taiga-ui/cdk/utils/miscellaneous';
import { TUI_NOTHING_FOUND_MESSAGE } from '@taiga-ui/core/tokens';
import { PolymorpheusOutlet } from '@taiga-ui/polymorpheus';
import { timer } from 'rxjs';
import { TUI_IS_MOBILE } from '@taiga-ui/cdk/tokens';
import { TuiDropdownDirective } from '@taiga-ui/core/directives/dropdown';
import * as i1 from '@taiga-ui/core/directives/icons';
import { TuiWithIcons } from '@taiga-ui/core/directives/icons';

/**
 * Accessor for data-list options
 */
const _c0 = ["tuiOption", ""];
const _c1 = ["*"];
const _c2 = a0 => ({
  $implicit: a0
});
function TuiOption_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const text_r1 = ctx.polymorpheusOutlet;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", text_r1, " ");
  }
}
function TuiOption_ng_template_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function TuiDataListComponent_div_1_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const text_r1 = ctx.polymorpheusOutlet;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", text_r1, " ");
  }
}
function TuiDataListComponent_div_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵtemplate(1, TuiDataListComponent_div_1_ng_container_1_Template, 2, 1, "ng-container", 2);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("polymorpheusOutlet", ctx_r1.emptyContent || ctx_r1.fallback());
  }
}
const TUI_DATA_LIST_ACCESSOR = tuiCreateToken();
function tuiAsDataListAccessor(accessor) {
  return tuiProvide(TUI_DATA_LIST_ACCESSOR, accessor);
}
/**
 * DataList controller
 */
const TUI_DATA_LIST_HOST = tuiCreateToken();
function tuiAsDataListHost(host) {
  return tuiProvide(TUI_DATA_LIST_HOST, host);
}

/**
 * Content for tuiOption component
 */
const TUI_OPTION_CONTENT = tuiCreateToken();
function tuiAsOptionContent(useValue) {
  return {
    provide: TUI_OPTION_CONTENT,
    useValue
  };
}
let TuiWithOptionContent = /*#__PURE__*/(() => {
  class TuiWithOptionContent {
    constructor() {
      this.localContent = null;
      this.globalContent = inject(TUI_OPTION_CONTENT, {
        optional: true
      });
    }
    get content() {
      return this.globalContent ?? this.localContent;
    }
    static {
      this.ɵfac = function TuiWithOptionContent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiWithOptionContent)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiWithOptionContent,
        contentQueries: function TuiWithOptionContent_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, TUI_OPTION_CONTENT, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.localContent = _t.first);
          }
        }
      });
    }
  }
  return TuiWithOptionContent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// TODO(v5): rename `TuiOptionNew` => `TuiOption` & remove [new] from selector
// TODO: Consider all use cases for aria roles
let TuiOptionNew = /*#__PURE__*/(() => {
  class TuiOptionNew {
    constructor() {
      this.vcr = inject(ViewContainerRef);
      this.isMobile = inject(TUI_IS_MOBILE);
      this.el = tuiInjectElement();
      this.dataList = inject(forwardRef(() => TuiDataListComponent), {
        optional: true
      });
      this.content = inject(TUI_OPTION_CONTENT, {
        optional: true
      });
      this.ref = this.content && createComponent(this.content, {
        environmentInjector: inject(EnvironmentInjector),
        elementInjector: inject(INJECTOR),
        hostElement: tuiInjectElement()
      });
      this.dropdown = inject(TuiDropdownDirective, {
        self: true,
        optional: true
      })?.ref;
      this.disabled = false;
      if (this.ref) {
        this.vcr.insert(this.ref.hostView);
        this.ref.changeDetectorRef.detectChanges();
      }
    }
    // Preventing focus loss upon focused option removal
    ngOnDestroy() {
      this.dataList?.handleFocusLossIfNecessary(this.el);
    }
    onMouseMove() {
      if (!this.isMobile && !tuiIsNativeFocused(this.el) && this.dataList && this.el.closest('[tuiDataListDropdownManager]')) {
        this.el.focus({
          preventScroll: true
        });
      }
    }
    static {
      this.ɵfac = function TuiOptionNew_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiOptionNew)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiOptionNew,
        selectors: [["button", "tuiOption", "", "new", ""], ["a", "tuiOption", "", "new", ""], ["label", "tuiOption", "", "new", ""]],
        hostAttrs: ["type", "button", "role", "option"],
        hostVars: 3,
        hostBindings: function TuiOptionNew_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("mousemove.zoneless", function TuiOptionNew_mousemove_zoneless_HostBindingHandler() {
              return ctx.onMouseMove();
            });
          }
          if (rf & 2) {
            i0.ɵɵattribute("disabled", ctx.disabled || null);
            i0.ɵɵclassProp("_with-dropdown", ctx.dropdown == null ? null : ctx.dropdown());
          }
        },
        inputs: {
          disabled: "disabled"
        },
        features: [i0.ɵɵHostDirectivesFeature([i1.TuiWithIcons])]
      });
    }
  }
  return TuiOptionNew;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
// TODO(v5): remove [new] from selector
let TuiOptionWithValue = /*#__PURE__*/(() => {
  class TuiOptionWithValue {
    constructor() {
      this.disabled = false;
      this.value = signal(undefined);
    }
    // TODO(v5): use `input.required<T>()` to remove `undefined` from `this.value()`
    set valueSetter(x) {
      this.value.set(x);
    }
    static {
      this.ɵfac = function TuiOptionWithValue_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiOptionWithValue)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiOptionWithValue,
        selectors: [["button", "tuiOption", "", "value", "", "new", ""], ["a", "tuiOption", "", "value", "", "new", ""], ["label", "tuiOption", "", "value", "", "new", ""]],
        inputs: {
          disabled: "disabled",
          valueSetter: [0, "value", "valueSetter"]
        }
      });
    }
  }
  return TuiOptionWithValue;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TODO(v5): delete
 * @deprecated use `<button tuiOption new />` / `<a tuiOption new /> / `<label tuiOption new /> instead
 */
let TuiOption = /*#__PURE__*/(() => {
  class TuiOption {
    constructor() {
      this.isMobile = inject(TUI_IS_MOBILE);
      this.el = tuiInjectElement();
      this.dataList = inject(forwardRef(() => TuiDataListComponent), {
        optional: true
      });
      this.host = inject(TUI_DATA_LIST_HOST, {
        optional: true
      });
      this.content = inject(TUI_OPTION_CONTENT, {
        optional: true
      });
      this.dropdown = inject(TuiDropdownDirective, {
        self: true,
        optional: true
      })?.ref;
      this.disabled = false;
    }
    // Preventing focus loss upon focused option removal
    ngOnDestroy() {
      this.dataList?.handleFocusLossIfNecessary(this.el);
    }
    onClick() {
      if (this.host?.handleOption && this.value !== undefined) {
        this.host.handleOption(this.value);
      }
    }
    onMouseMove() {
      if (!this.isMobile && !tuiIsNativeFocused(this.el) && this.dataList && this.el.closest('[tuiDataListDropdownManager]')) {
        this.el.focus({
          preventScroll: true
        });
      }
    }
    static {
      this.ɵfac = function TuiOption_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiOption)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiOption,
        selectors: [["button", "tuiOption", "", 3, "new", ""], ["a", "tuiOption", "", 3, "new", ""], ["label", "tuiOption", "", 3, "new", ""]],
        hostAttrs: ["type", "button", "role", "option"],
        hostVars: 3,
        hostBindings: function TuiOption_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function TuiOption_click_HostBindingHandler() {
              return ctx.onClick();
            })("mousemove.zoneless", function TuiOption_mousemove_zoneless_HostBindingHandler() {
              return ctx.onMouseMove();
            });
          }
          if (rf & 2) {
            i0.ɵɵattribute("disabled", ctx.disabled || null);
            i0.ɵɵclassProp("_with-dropdown", ctx.dropdown == null ? null : ctx.dropdown());
          }
        },
        inputs: {
          disabled: "disabled",
          value: "value"
        },
        features: [i0.ɵɵHostDirectivesFeature([i1.TuiWithIcons])],
        attrs: _c0,
        ngContentSelectors: _c1,
        decls: 3,
        vars: 4,
        consts: [["t", ""], [4, "polymorpheusOutlet", "polymorpheusOutletContext"]],
        template: function TuiOption_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵtemplate(0, TuiOption_ng_container_0_Template, 2, 1, "ng-container", 1)(1, TuiOption_ng_template_1_Template, 1, 0, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
          }
          if (rf & 2) {
            const t_r2 = i0.ɵɵreference(2);
            i0.ɵɵproperty("polymorpheusOutlet", ctx.content || t_r2)("polymorpheusOutletContext", i0.ɵɵpureFunction1(2, _c2, t_r2));
          }
        },
        dependencies: [PolymorpheusOutlet],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return TuiOption;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function tuiInjectDataListSize() {
  const sizes = ['s', 'm', 'l'];
  const size = inject(TUI_DATA_LIST_HOST, {
    optional: true
  })?.size;
  return size && sizes.includes(size) ? size : 'l';
}
// TODO: Consider aria-activedescendant for proper accessibility implementation
let TuiDataListComponent = /*#__PURE__*/(() => {
  class TuiDataListComponent {
    constructor() {
      this.legacyOptions = EMPTY_QUERY;
      this.options = EMPTY_QUERY;
      this.ngZone = inject(NgZone);
      this.destroyRef = inject(DestroyRef);
      this.el = tuiInjectElement();
      this.cdr = inject(ChangeDetectorRef);
      this.fallback = toSignal(inject(TUI_NOTHING_FOUND_MESSAGE));
      this.empty = signal(false);
      this.size = tuiInjectDataListSize();
    }
    onKeyDownArrow(current, step) {
      const {
        elements
      } = this;
      tuiMoveFocus(elements.indexOf(current), elements, step);
    }
    handleFocusLossIfNecessary(element = this.el) {
      if (tuiIsNativeFocusedIn(element)) {
        this.origin?.focus({
          preventScroll: true
        });
      }
    }
    // TODO: Refactor to :has after Safari support bumped to 15
    ngAfterContentChecked() {
      timer(0).pipe(tuiZonefree(this.ngZone), tuiTakeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.empty.set(!this.elements.length);
        this.cdr.detectChanges();
      });
    }
    getOptions(includeDisabled = false) {
      return [...this.legacyOptions, ...this.options].filter(({
        disabled
      }) => includeDisabled || !disabled).map(({
        value
      }) => isSignal(value) ? value() : value).filter(tuiIsPresent);
    }
    onFocusIn(relatedTarget, currentTarget) {
      if (!currentTarget.contains(relatedTarget) && !this.origin) {
        this.origin = relatedTarget;
      }
    }
    get elements() {
      return Array.from(this.el.querySelectorAll('[tuiOption]'));
    }
    static {
      this.ɵfac = function TuiDataListComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiDataListComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiDataListComponent,
        selectors: [["tui-data-list"]],
        contentQueries: function TuiDataListComponent_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, TuiOption, 5);
            i0.ɵɵcontentQuery(dirIndex, TuiOptionWithValue, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.legacyOptions = _t);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.options = _t);
          }
        },
        hostAttrs: ["role", "listbox"],
        hostVars: 1,
        hostBindings: function TuiDataListComponent_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("focusin", function TuiDataListComponent_focusin_HostBindingHandler($event) {
              return ctx.onFocusIn($event.relatedTarget, $event.currentTarget);
            })("mousedown.prevent", function TuiDataListComponent_mousedown_prevent_HostBindingHandler() {
              return 0;
            })("wheel.zoneless.passive", function TuiDataListComponent_wheel_zoneless_passive_HostBindingHandler() {
              return ctx.handleFocusLossIfNecessary();
            })("mouseleave", function TuiDataListComponent_mouseleave_HostBindingHandler($event) {
              return ctx.handleFocusLossIfNecessary($event.target);
            })("keydown.tab", function TuiDataListComponent_keydown_tab_HostBindingHandler() {
              return ctx.handleFocusLossIfNecessary();
            })("keydown.shift.tab", function TuiDataListComponent_keydown_shift_tab_HostBindingHandler() {
              return ctx.handleFocusLossIfNecessary();
            })("keydown.arrowDown.prevent", function TuiDataListComponent_keydown_arrowDown_prevent_HostBindingHandler($event) {
              return ctx.onKeyDownArrow($event.target, 1);
            })("keydown.arrowUp.prevent", function TuiDataListComponent_keydown_arrowUp_prevent_HostBindingHandler($event) {
              return ctx.onKeyDownArrow($event.target, -1);
            });
          }
          if (rf & 2) {
            i0.ɵɵattribute("data-size", ctx.size);
          }
        },
        inputs: {
          emptyContent: "emptyContent",
          size: "size"
        },
        features: [i0.ɵɵProvidersFeature([tuiAsDataListAccessor(TuiDataListComponent), {
          provide: TUI_OPTION_CONTENT,
          useFactory: () => inject(TuiWithOptionContent, {
            optional: true
          })?.content ?? inject(TUI_OPTION_CONTENT, {
            skipSelf: true,
            optional: true
          })
        }])],
        ngContentSelectors: _c1,
        decls: 2,
        vars: 1,
        consts: [["class", "t-empty", 4, "ngIf"], [1, "t-empty"], [4, "polymorpheusOutlet"]],
        template: function TuiDataListComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
            i0.ɵɵtemplate(1, TuiDataListComponent_div_1_Template, 2, 1, "div", 0);
          }
          if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.empty());
          }
        },
        dependencies: [NgIf, PolymorpheusOutlet],
        styles: ["tui-data-list{--tui-data-list-padding: .25rem;--tui-data-list-margin: .0625rem;display:flex;font:var(--tui-font-text-m);flex-direction:column;padding:calc(var(--tui-data-list-padding) - var(--tui-data-list-margin)) var(--tui-data-list-padding);color:var(--tui-text-tertiary)}tui-data-list:focus-within .t-trap{display:none}tui-data-list:focus-within [tuiOption]._with-dropdown:not(:focus){background-color:transparent}tui-data-list[data-size=s]{--tui-data-list-margin: 0rem}tui-data-list[data-size=s] [tuiOption][new]>*:not(:first-child){margin-inline-start:.5rem}tui-data-list[data-size=s]>.t-empty,tui-data-list[data-size=s] [tuiOption]{--t-option-padding-inline: .5rem;font:var(--tui-font-text-s);min-block-size:2rem;padding-top:.3125rem;padding-bottom:.3125rem}tui-data-list[data-size=s]>.t-empty:before,tui-data-list[data-size=s] [tuiOption]:before{font-size:1rem}tui-data-list[data-size=m] [tuiOption][new]>*:not(:first-child){margin-inline-start:.75rem}tui-data-list[data-size=m]>.t-empty,tui-data-list[data-size=m] [tuiOption]{--t-option-padding-inline: .5rem;font:var(--tui-font-text-s);min-block-size:2.5rem;padding-top:.375rem;padding-bottom:.375rem}tui-data-list[data-size=l]{--tui-data-list-padding: .375rem;--tui-data-list-margin: .125rem}tui-data-list[data-size=l] [tuiOption][new]>*:not(:first-child){margin-inline-start:1rem}tui-data-list[data-size=l]>.t-empty,tui-data-list[data-size=l] [tuiOption]{--t-option-padding-inline: .625rem;font:var(--tui-font-text-m);min-block-size:2.75rem;padding-top:.375rem;padding-bottom:.375rem}tui-data-list>.t-empty{display:flex;align-items:center;box-sizing:border-box;margin:var(--tui-data-list-margin) 0}tui-data-list [tuiOption]:not([new]){justify-content:space-between}tui-data-list [tuiOption]{-webkit-appearance:none;appearance:none;padding:0;border:0;background:none;font:inherit;line-height:inherit;text-decoration:none;transition-property:background;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;display:flex;align-items:center;box-sizing:border-box;margin:var(--tui-data-list-margin) 0;text-align:start;color:var(--tui-text-primary);border-radius:var(--tui-radius-s);outline:none;cursor:pointer;background-clip:padding-box}tui-data-list [tuiOption]:disabled{opacity:var(--tui-disabled-opacity);cursor:default}@media (hover: hover) and (pointer: fine){tui-data-list [tuiOption]:hover:not(:disabled){background-color:var(--tui-background-neutral-1)}}tui-data-list [tuiOption]:active:not(:disabled),tui-data-list [tuiOption]:focus-within,tui-data-list [tuiOption]._with-dropdown{background-color:var(--tui-background-neutral-1)}tui-data-list [tuiOption]:before{margin-inline-end:.5rem}tui-data-list [tuiOption]:after{font-size:1rem;margin:0 -.75rem 0 auto;border-left:.5rem solid;border-right:.5rem solid}tui-data-list>.t-empty,tui-data-list [tuiOption]{padding-left:var(--t-option-padding-inline);padding-right:var(--t-option-padding-inline)}tui-opt-group{position:relative;display:flex;font:var(--tui-font-text-xs);font-weight:700;color:var(--tui-text-primary);flex-direction:column;line-height:1rem}tui-data-list[data-size=l] tui-opt-group{font:var(--tui-font-text-s);font-weight:700;line-height:1.25rem}tui-data-list[data-size=l] tui-opt-group:before{padding-left:.625rem;padding-right:.625rem}tui-data-list[data-size=l] tui-opt-group:after{left:.625rem;right:.625rem}tui-opt-group:empty:before,tui-opt-group:empty:after{display:none}tui-opt-group:before{content:attr(data-label);padding:var(--tui-data-list-padding) .5rem var(--tui-data-list-padding);margin:var(--tui-data-list-margin) 0;white-space:normal;word-break:break-word}tui-opt-group:after{position:absolute;left:.5rem;right:.5rem;top:var(--tui-data-list-padding);block-size:1px;background:var(--tui-border-normal)}tui-opt-group:not(:empty)~tui-opt-group:before{padding-top:calc(.75rem + var(--tui-data-list-padding))}tui-opt-group:not(:empty)~tui-opt-group[data-label=\"\"]:before,tui-opt-group:not(:empty)~tui-opt-group:not([data-label]):before{padding:var(--tui-data-list-padding) 0}tui-opt-group:not(:empty)~tui-opt-group:after{content:\"\"}tui-opt-group[data-label=\"\"]:before,tui-opt-group:not([data-label]):before{content:\"\";padding:0;margin:0}\n"],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return TuiDataListComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiDataListDirective = /*#__PURE__*/(() => {
  class TuiDataListDirective {
    static {
      this.ɵfac = function TuiDataListDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiDataListDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiDataListDirective,
        selectors: [["ng-template", "tuiDataList", ""]]
      });
    }
  }
  return TuiDataListDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function tuiAsDataList(list) {
  return tuiProvide(TuiDataListDirective, list);
}
let TuiOptGroup = /*#__PURE__*/(() => {
  class TuiOptGroup {
    static {
      this.ɵfac = function TuiOptGroup_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiOptGroup)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiOptGroup,
        selectors: [["tui-opt-group"]],
        hostAttrs: ["role", "group"],
        hostVars: 1,
        hostBindings: function TuiOptGroup_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("data-label", ctx.label);
          }
        },
        inputs: {
          label: "label"
        }
      });
    }
  }
  return TuiOptGroup;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const TuiDataList = [TuiDataListComponent, TuiDataListDirective, TuiOption, TuiOptionNew, TuiOptionWithValue, TuiOptGroup];

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_DATA_LIST_ACCESSOR, TUI_DATA_LIST_HOST, TUI_OPTION_CONTENT, TuiDataList, TuiDataListComponent, TuiDataListDirective, TuiOptGroup, TuiOption, TuiOptionNew, TuiOptionWithValue, TuiWithOptionContent, tuiAsDataList, tuiAsDataListAccessor, tuiAsDataListHost, tuiAsOptionContent, tuiInjectDataListSize };
